import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "next-themes";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { MainLayout } from "@/components/layout/MainLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { User, Mail, Lock, LogOut, Save, Camera, Moon, Sun, Briefcase, Bot } from "lucide-react";

interface ProfileData {
  first_name: string;
  last_name: string;
  full_name: string | null;
  work_ai_platform: string;
  work_ai_platform_other: string;
}

const DEPARTMENTS = [
  "Business Development",
  "Consulting",
  "Strategy",
  "Events",
  "Ops",
  "Leadership",
];

const AI_PLATFORMS = [
  "ChatGPT",
  "Gemini",
  "Claude",
  "Copilot",
  "Perplexity",
  "Other",
];

export default function ProfileSettings() {
  const { user, membership, signOut, refreshMembership } = useAuth();
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [department, setDepartment] = useState("");
  const [profile, setProfile] = useState<ProfileData>({
    first_name: "",
    last_name: "",
    full_name: null,
    work_ai_platform: "",
    work_ai_platform_other: "",
  });

  useEffect(() => {
    if (user) {
      fetchProfile();
    }
  }, [user]);

  useEffect(() => {
    setDepartment(membership?.department || "");
  }, [membership?.department]);

  const fetchProfile = async () => {
    if (!user) return;
    setLoading(true);

    const { data, error } = await supabase
        .from("profiles")
      .select("first_name, last_name, full_name, work_ai_platform, work_ai_platform_other")
      .eq("user_id", user.id)
      .single();

    if (error) {
      console.error("Error fetching profile:", error);
    } else if (data) {
      setProfile({
        first_name: data.first_name || "",
        last_name: data.last_name || "",
        full_name: data.full_name,
        work_ai_platform: data.work_ai_platform || "",
        work_ai_platform_other: data.work_ai_platform_other || "",
      });
    }
    setDepartment(membership?.department || "");
    setLoading(false);
  };

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);

    const fullName = [profile.first_name, profile.last_name].filter(Boolean).join(" ");

    const { error } = await supabase
      .from("profiles")
      .update({
        first_name: profile.first_name,
        last_name: profile.last_name,
        full_name: fullName || null,
        work_ai_platform: profile.work_ai_platform || null,
        work_ai_platform_other: profile.work_ai_platform === "Other" ? profile.work_ai_platform_other || null : null,
      })
      .eq("user_id", user.id);

    let membershipError = null;
    if (!error && membership?.org_id && department) {
      const { error: updateError } = await supabase
        .from("org_memberships")
        .update({ department: department as "Business Development" | "Consulting" | "Strategy" | "Events" | "Ops" | "Leadership" })
        .eq("user_id", user.id)
        .eq("org_id", membership.org_id);
      membershipError = updateError;
    }

    if (error || membershipError) {
      toast.error("Failed to save profile");
      console.error("Error saving profile:", error || membershipError);
    } else {
      await refreshMembership();
      toast.success("Profile saved successfully");
    }
    setSaving(false);
  };

  const handleResetPassword = async () => {
    if (!user?.email) return;
    setResettingPassword(true);

    const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
      redirectTo: `${window.location.origin}/set-new-password`,
    });

    if (error) {
      toast.error("Failed to send reset email");
      console.error("Error sending reset email:", error);
    } else {
      toast.success("Password reset email sent! Check your inbox.");
    }
    setResettingPassword(false);
  };

  const handleLogout = async () => {
    await signOut();
    navigate("/login");
  };

  const getInitials = () => {
    const first = profile.first_name?.[0] || "";
    const last = profile.last_name?.[0] || "";
    return (first + last).toUpperCase() || "U";
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="p-4 md:p-6 max-w-2xl mx-auto space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-64 w-full" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="p-4 md:p-6 max-w-2xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-display font-bold">Profile Settings</h1>
          <p className="text-muted-foreground mt-1">Manage your account and preferences</p>
        </div>

        {/* Profile Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Profile Information
            </CardTitle>
            <CardDescription>Update your personal details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Avatar Section */}
            <div className="flex items-center gap-4">
              <Avatar className="h-20 w-20 border-2 border-primary/20">
                <AvatarFallback className="text-xl bg-muted">{getInitials()}</AvatarFallback>
              </Avatar>
              <div>
                <Button variant="outline" size="sm" disabled>
                  <Camera className="h-4 w-4 mr-2" />
                  Upload Photo
                </Button>
                <p className="text-xs text-muted-foreground mt-1">Coming soon</p>
              </div>
            </div>

            {/* Name Fields */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name</Label>
                <Input
                  id="first_name"
                  value={profile.first_name}
                  onChange={(e) => setProfile({ ...profile, first_name: e.target.value })}
                  placeholder="Enter your first name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name</Label>
                <Input
                  id="last_name"
                  value={profile.last_name}
                  onChange={(e) => setProfile({ ...profile, last_name: e.target.value })}
                  placeholder="Enter your last name"
                />
              </div>
            </div>

            {/* Email (read-only) */}
            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Email
              </Label>
              <Input
                id="email"
                value={user?.email || ""}
                disabled
                className="bg-muted"
              />
              <p className="text-xs text-muted-foreground">Email cannot be changed</p>
            </div>

            {/* Work Context */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="department" className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4" />
                  Role
                </Label>
                <Select value={department} onValueChange={setDepartment}>
                  <SelectTrigger id="department">
                    <SelectValue placeholder="Select your role" />
                  </SelectTrigger>
                  <SelectContent>
                    {DEPARTMENTS.map((dept) => (
                      <SelectItem key={dept} value={dept}>
                        {dept}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="work_ai_platform" className="flex items-center gap-2">
                  <Bot className="h-4 w-4" />
                  AI tool used most
                </Label>
                <Select
                  value={profile.work_ai_platform}
                  onValueChange={(value) => setProfile({ ...profile, work_ai_platform: value })}
                >
                  <SelectTrigger id="work_ai_platform">
                    <SelectValue placeholder="Select an AI tool" />
                  </SelectTrigger>
                  <SelectContent>
                    {AI_PLATFORMS.map((platform) => (
                      <SelectItem key={platform} value={platform}>
                        {platform}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {profile.work_ai_platform === "Other" && (
              <div className="space-y-2">
                <Label htmlFor="work_ai_platform_other">Other AI tool</Label>
                <Input
                  id="work_ai_platform_other"
                  value={profile.work_ai_platform_other}
                  onChange={(e) => setProfile({ ...profile, work_ai_platform_other: e.target.value })}
                  placeholder="Enter the AI tool you use most"
                />
              </div>
            )}

            {/* Save Button */}
            <Button onClick={handleSave} disabled={saving}>
              <Save className="h-4 w-4 mr-2" />
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </CardContent>
        </Card>

        {/* Security Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              Security
            </CardTitle>
            <CardDescription>Manage your password and security settings</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Password</p>
                <p className="text-sm text-muted-foreground">Reset your account password</p>
              </div>
              <Button
                variant="outline"
                onClick={handleResetPassword}
                disabled={resettingPassword}
              >
                {resettingPassword ? "Sending..." : "Reset Password"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Preferences Card */}
        <Card>
          <CardHeader>
            <CardTitle>Preferences</CardTitle>
            <CardDescription>Customize your experience</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {theme === 'dark' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
                <div>
                  <p className="font-medium">Appearance</p>
                  <p className="text-sm text-muted-foreground">
                    {theme === 'dark' ? 'Dark mode' : 'Light mode'}
                  </p>
                </div>
              </div>
              <Switch
                checked={theme === 'dark'}
                onCheckedChange={(checked) => setTheme(checked ? 'dark' : 'light')}
              />
            </div>
          </CardContent>
        </Card>

        <Separator />

        {/* Logout */}
        <div className="flex justify-end">
          <Button variant="destructive" onClick={handleLogout}>
            <LogOut className="h-4 w-4 mr-2" />
            Log Out
          </Button>
        </div>
      </div>
    </MainLayout>
  );
}
